import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Link, Grid } from "@mui/material";
import api from "api";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import FirstLineContainer from "components/common/FirstLineContainer";
import AddPoliciesPopup from "./AddPoliciesPopup";
import StyledDataGrid from "styles/styledDataGrid";
import ReleaseSignOff from "./ReleaseSignOff";
import ViewPoliciesPopup from "./ViewPoliciesPopup";
import { BUILD, REGRESSION } from "constants/Constants";

function ReleasePlanDetails() {
  const { releaseKey, releaseNumber } = useParams(); // Get ID from URL
  const [releaseData, setReleaseData] = useState(null);
  const [canUpdatePolicies, setCanUpdatePolicies] = useState(false);
  const [releaseDetailsData, setReleaseDetailsData] = useState([]);
  const [allAvailablePolicies, setAllAvailablePolicies] = useState([]);
  const [readyForBuild, setReadyForBuild] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // const editable = location.state?.editable ?? false;
  const [openAddPopup, setOpenAddPopup] = useState(false);
  const [openViewPopup, setOpenViewPopup] = useState(false);
  const [popupPolicies, setPopupPolicies] = useState([]);
  const [popupTitle, setPopupTitle] = useState("");
  const dispatch = useDispatch();

  const fetchReleaseDetails = useCallback(async () => {
    try {
      const response =
        await api.ReleaseSchedule.getReleaseScheduleDetails(releaseKey);
      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          response.data?.errors ||
            "Failed to retrieve the Release Schedule Details"
        );
      }
      setReleaseData(response.data);
      setReleaseDetailsData(buildReleaseDetailsData(response.data));
      setCanUpdatePolicies(
        response.data.pipelineStatusText === BUILD ||
          response.data.pipelineStatusText === REGRESSION
      );

      const policyResponse =
        await api.ReleaseSchedule.getPolicyMappingDetails(releaseKey);
      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          response.data?.errors || "Failed to load mapped policies"
        );
      }
      const allPolicies = policyResponse.data;

      if (response.data?.policies?.length > 0) {
        const releasePolicyKeys = new Set(
          response.data.policies.map((policy) => policy.policyKey)
        );
        const filteredPolicies = allPolicies.filter(
          (policy) => !releasePolicyKeys.has(policy.policyKey)
        );
        setAllAvailablePolicies(filteredPolicies);
      } else {
        setAllAvailablePolicies(allPolicies);
      }
    } catch (error) {
      dispatch(
        setDialog({
          message:
            error.response?.data?.errors ||
            "Failed to retrieve the List of Release Plans",
          title: "Error",
        })
      );
      console.error("Error fetching release details:", error);
    } finally {
      setLoading(false);
    }
  }, [releaseKey, dispatch]);

  useEffect(() => {
    fetchReleaseDetails();
  }, [fetchReleaseDetails]);

  const reloadParent = () => {
    fetchReleaseDetails();
  };

  const buildReleaseDetailsData = (releaseData) => {
    if (!releaseData || !releaseData.policies) return [];

    const policyGroups = releaseData.policies.reduce((groups, policy) => {
      const type = policy.policyType?.trim() || "Unknown";

      if (!groups[type]) {
        groups[type] = {
          type,
          total: [],
          coding: [],
          translation: [],
          configuration: [],
          codingDone: [],
          translationDone: [],
          configurationDone: [],
          ready: [],
        };
      }

      groups[type].total.push(policy);
      const { codingStatus, translationStatus, configurationStatus } = policy;

      // Categorizing policies based on their status
      if (codingStatus === "Complete") groups[type].codingDone.push(policy);
      else groups[type].coding.push(policy);

      if (translationStatus === "Complete")
        groups[type].translationDone.push(policy);
      else groups[type].translation.push(policy);

      if (configurationStatus === "Complete")
        groups[type].configurationDone.push(policy);
      else groups[type].configuration.push(policy);

      if (
        codingStatus === "Complete" &&
        translationStatus === "Complete" &&
        configurationStatus === "Complete"
      ) {
        groups[type].ready.push(policy);
      }

      return groups;
    }, {});

    // Convert grouped object into an array
    const releaseDetailsData = Object.entries(policyGroups).map(
      ([type, data], index) => ({
        id: index + 1,
        type,
        total: data.total.length,
        coding: data.coding.length,
        translation: data.translation.length,
        configuration: data.configuration.length,
        codingDone: data.codingDone.length,
        translationDone: data.translationDone.length,
        configurationDone: data.configurationDone.length,
        ready: data.ready.length,
        policyDetails: data,
      })
    );

    // Determine if all policies are ready
    const totalPolicies = releaseData.policies.length;
    const totalReady = releaseDetailsData.reduce(
      (sum, row) => sum + row.ready,
      0
    );

    setReadyForBuild(totalPolicies > 0 && totalReady === totalPolicies);

    // Compute Overall % Ready Row
    const computePercentage = (count) =>
      totalPolicies > 0
        ? `${Math.round((count / totalPolicies) * 100)}%`
        : "0%";

    const percentageRow = {
      id: releaseDetailsData.length + 1,
      type: "% Ready",
      total: "",
      coding: computePercentage(
        releaseDetailsData.reduce((sum, row) => sum + row.codingDone, 0)
      ),
      translation: computePercentage(
        releaseDetailsData.reduce((sum, row) => sum + row.translationDone, 0)
      ),
      configuration: computePercentage(
        releaseDetailsData.reduce((sum, row) => sum + row.configurationDone, 0)
      ),
      ready: computePercentage(
        releaseDetailsData.reduce((sum, row) => sum + row.ready, 0)
      ),
      policyDetails: Object.fromEntries(
        [
          "total",
          "codingDone",
          "translationDone",
          "configurationDone",
          "ready",
        ].map((key) => [
          key,
          releaseDetailsData.flatMap((row) => row.policyDetails[key]),
        ])
      ),
    };

    releaseDetailsData.push(percentageRow);
    return releaseDetailsData;
  };

  const handleCellClick = (params) => {
    if (params.field === "type") return;

    const row = params.row;
    let category = params.field;
    const catNameForTitle =
      params.field.charAt(0).toUpperCase() + params.field.slice(1);
    if ("% Ready" === row.type) category = category + "Done";
    const policies = row.policyDetails[category] || [];

    setPopupPolicies(policies);
    setPopupTitle(
      `${catNameForTitle} Policies for ${row.type} (${policies.length})`
    ); // Include count
    setOpenViewPopup(true);
  };

  if (loading) return <CircularProgress />;

  const columns = [
    { field: "type", headerName: "Type", flex: 1, headerAlign: "center" },
    ...["total", "coding", "translation", "configuration", "ready"].map(
      (field) => ({
        field,
        headerName: field.charAt(0).toUpperCase() + field.slice(1),
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => handleCellClick(params)}
          >
            {params.value}
          </Link>
        ),
      })
    ),
  ];

  const sendBackToMainPage = () => {
    navigate("/release-plan");
  };

  return (
    <>
      {releaseKey && (
        <>
          <FirstLineContainer>
            <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
              <Box component="span" fontWeight="fontWeightBold">
                {releaseNumber} - Pipeline
              </Box>
            </Typography>
          </FirstLineContainer>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            {/* ======= Release Details Section ======= */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mr: 5,
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  Release Details
                </Typography>
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => setOpenAddPopup(true)}
                >
                  Add Policies
                </Link>
              </Box>
              <Box sx={{ height: 200, width: "100%", mb: 2 }}>
                <StyledDataGrid
                  columns={columns}
                  rows={releaseDetailsData}
                  rowHeight={40}
                  disableColumnMenu
                  hideFooter
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f5f5f5",
                      fontWeight: "bold",
                    },
                    "& .MuiDataGrid-cell": { textAlign: "center" },
                    "& .bold-text": {
                      fontWeight: "bold",
                    },
                  }}
                />
              </Box>

              {/* ======= Release Pipeline Section ======= */}
              <Typography variant="h5" fontWeight="bold">
                Release Pipeline
              </Typography>
              <ReleaseSignOff
                releaseData={releaseData}
                enableChangeRequired={canUpdatePolicies}
                readyForBuild={readyForBuild}
                reloadParent={reloadParent}
              ></ReleaseSignOff>
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", ml: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mr: 5,
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  Release History
                </Typography>
                <Link
                  component="button"
                  variant="body1"
                  onClick={sendBackToMainPage}
                >
                  Back to List
                </Link>
              </Box>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  p: 2,
                  height: 275,
                }}
              ></Box>
            </Box>
          </Box>
          <AddPoliciesPopup
            open={openAddPopup}
            handleClose={() => setOpenAddPopup(false)}
            reloadParent={reloadParent}
            releaseKey={releaseKey}
            releaseNumber={releaseNumber}
            mappedPolicies={releaseData?.policies}
            availablePolicies={allAvailablePolicies}
            canUpdatePolicies={canUpdatePolicies}
          />
          <ViewPoliciesPopup
            open={openViewPopup}
            handleClose={() => setOpenViewPopup(false)}
            reloadParent={reloadParent}
            releaseKey={releaseKey}
            title={popupTitle}
            policies={popupPolicies}
            canUpdatePolicies={canUpdatePolicies}
          />
        </>
      )}
    </>
  );
}

export default ReleasePlanDetails;
